import { createFileRoute, redirect } from "@tanstack/react-router";
import { Page } from "@core/components/layout/page";
import { ValueVector } from "@core/components/value-vector";
import {
  clientDetailsQueryOptions,
  portfolioCategorySummaryOptions,
  portfolioSummaryV2QueryOptions,
  useSipData,
  useSwpData,
  useSTPPlans,
  useInvestorBankAccounts,
  usePortfolioCategorySummary,
  usePortfolioSummaryV2,
  useSubmittedOrders,
} from "creso-core/hooks";
import {
  findBreakdownByCategory,
  formatAsCrore,
  isActiveClient,
  getPortfolioDistributionFromBreakdown,
  getAssetAllocationBucketsFromBreakdown,
} from "creso-core/utils";
import { TransactionTrigger } from "@core/components/transaction-trigger";
import {
  cn,
  Tabs,
  TabsList,
  TabsTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  V2Loader,
} from "web-design-system";
import { useMemo, useState } from "react";
import {
  ASSET_ALLOCATION_BUCKET_COLORS,
  AssetAllocation,
} from "@crm/components/overview/asset-allocation";
import { PortfolioDistribution } from "@crm/components/overview/portfolio-distribution";
import { OverviewProductBreakdown } from "@crm/components/overview/product-breakdown";
import { OverviewReports } from "@crm/components/overview/reports";
import { ConditionalRenderer } from "@core/components/conditional-renderer";
import { WhatNext } from "@crm/components/overview/what-next";
import { StatTile } from "@crm/components/overview/stat-title";
import { OverviewRecentActivity } from "@crm/components/overview/recent-activity";
import { EmptyExternalPortfolio } from "@crm/components/portfolio/no-external-holdings";
import { PortfolioImportStatusComponent } from "@crm/components/portfolio/summary";
import { PortfolioBreakdown, PortfolioSource } from "creso-core/types";

export const Route = createFileRoute("/(auth)/_headed/clients/$id/")({
  component: Overview,
  beforeLoad: async ({ context, params }) => {
    const { queryClient } = context;

    if (Number.isNaN(Number(params.id))) {
      throw Error("Invalid client Id");
    }

    const client = await queryClient.ensureQueryData(
      clientDetailsQueryOptions(Number(params.id))
    );

    if (client.status === "LEAD" || client.status === "VERIFICATION_PENDING") {
      throw redirect({
        to: "/clients/$id/planning",
        params,
      });
    }

    const summary = await queryClient.ensureQueryData(
      portfolioSummaryV2QueryOptions(client?.investment_account ?? "")
    );

    const overallSummary = summary?.find((p) => p.source_type === "OVERALL");

    const hasAum = Number(overallSummary?.invested_value) > 0;

    if (client.status === "SETUP_PENDING" && !hasAum) {
      throw redirect({
        to: "/clients/$id/portfolio",
        params,
      });
    }
  },
  loader: async ({ context, params }) => {
    const { queryClient } = context;

    if (Number.isNaN(Number(params.id))) {
      throw Error("Invalid client Id");
    }

    const client = await queryClient.ensureQueryData(
      clientDetailsQueryOptions(Number(params.id))
    );

    if (!client.investment_account) {
      throw Error("Missing investment account.");
    }

    const [portfolioSummaryResult, portfolioBreakdownResult] =
      await Promise.allSettled([
        queryClient.ensureQueryData(
          portfolioSummaryV2QueryOptions(client.investment_account)
        ),
        queryClient.ensureQueryData(
          portfolioCategorySummaryOptions({
            investmentAccountId: client.investment_account,
          })
        ),
      ]);

    if (
      portfolioSummaryResult.status === "rejected" ||
      portfolioBreakdownResult.status === "rejected"
    ) {
      throw Error("Could not fetch portfolio summary");
    }

    return {
      client,
      portfolioSummary: portfolioSummaryResult.value,
      portfolioBreakdown: portfolioBreakdownResult.value,
    };
  },
});

function Overview() {
  const {
    portfolioSummary: initialSummary,
    client,
    portfolioBreakdown: initialBreakdown,
  } = Route.useLoaderData();

  const { data: portfolioSummary } = usePortfolioSummaryV2(
    client.investment_account || "",
    !!client.investment_account,
    initialSummary
  );

  const { data: portfolioBreakdown } = usePortfolioCategorySummary(
    { investmentAccountId: client.investment_account ?? undefined },
    { initialData: initialBreakdown, enabled: !!client.investment_account }
  );

  const { data: sip, isLoading: sipLoading } = useSipData(
    client.investment_account ?? "",
    1,
    1
  );
  const { data: swp, isLoading: swpLoading } = useSwpData(
    client.investment_account ?? "",
    1,
    1
  );
  const { data: stp, isLoading: stpLoading } = useSTPPlans(
    client.investment_account ?? "",
    1,
    1
  );

  const { data: bankAccounts } = useInvestorBankAccounts(
    client.investment_account ?? ""
  );

  const { data: orders, isLoading: isLoadingOrders } = useSubmittedOrders({
    investment_account_guid: client.investment_account || "",
    page: 1,
    pageSize: 1,
  });

  const hasPlacedTransaction = Number(orders?.count) > 0;

  const hasAtleastOneVerifiedBankAccount = useMemo(
    () => bankAccounts?.some((b) => b.verification_status === "verified"),
    [bankAccounts]
  );

  const canClientTransact =
    (client?.status === "ACTIVE" || client?.status === "UPDATE_PENDING") &&
    hasAtleastOneVerifiedBankAccount;

  const clientIsActive = isActiveClient(client);

  const isLoading = sipLoading || swpLoading || stpLoading;

  const externalSummary = useMemo(
    () => portfolioSummary?.find((p) => p.source_type === "EXTERNAL"),
    [portfolioSummary]
  );
  const internalSummary = useMemo(
    () => portfolioSummary?.find((p) => p.source_type === "INTERNAL"),
    [portfolioSummary]
  );

  const hasExternalAum = Number(externalSummary?.invested_value) > 0;

  const hasInternalAum = Number(internalSummary?.invested_value) > 0;

  const showOverallSummary = hasExternalAum && hasInternalAum;

  const [sourceType, setSourceType] = useState(
    showOverallSummary ? "OVERALL" : hasExternalAum ? "EXTERNAL" : "INTERNAL"
  );

  const summary = useMemo(
    () => portfolioSummary?.find((s) => s.source_type === sourceType),
    [sourceType, portfolioSummary]
  );

  const hasAum = Number(summary?.invested_value) > 0;

  const breakdownBySourceType = useMemo(
    () =>
      findBreakdownByCategory(
        portfolioBreakdown as PortfolioBreakdown[],
        sourceType
      ),
    [sourceType, portfolioBreakdown]
  );

  const allocationBuckets = useMemo(
    () =>
      getAssetAllocationBucketsFromBreakdown(
        breakdownBySourceType,
        ASSET_ALLOCATION_BUCKET_COLORS
      ),
    [breakdownBySourceType]
  );

  const portfolioDistribution = useMemo(
    () => getPortfolioDistributionFromBreakdown(breakdownBySourceType),
    [breakdownBySourceType]
  );

  const currentValue = Number(summary?.current_value);
  const investedValue = Number(summary?.invested_value);
  const profitLoss = Number(summary?.profit_loss);
  const returnPct = summary?.absolute_return_pct;

  return (
    <>
      <Page.Header
        className="relative"
        title="Overview"
        actions={
          canClientTransact ? (
            <TransactionTrigger
              label="Transaction"
              clientId={client.id}
              variant="default"
            />
          ) : null
        }
      />
      <Page.Body>
        <V2Loader isLoading={isLoading}>
          <Tabs
            value={sourceType}
            onValueChange={setSourceType}
            className="mb-4"
          >
            <div className="flex flex-row items-center justify-between">
              <TabsList>
                {showOverallSummary && (
                  <TabsTrigger value="OVERALL">All</TabsTrigger>
                )}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span
                      className={cn(
                        "inline-flex",
                        !hasInternalAum &&
                          !clientIsActive &&
                          "cursor-not-allowed"
                      )}
                    >
                      <TabsTrigger
                        value="INTERNAL"
                        disabled={!hasInternalAum && !clientIsActive}
                        className={
                          !hasInternalAum && !clientIsActive
                            ? "pointer-events-none"
                            : ""
                        }
                      >
                        Internal
                      </TabsTrigger>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent hidden={clientIsActive} side="top">
                    Activate the account to start transacting.
                  </TooltipContent>
                </Tooltip>
                <TabsTrigger value="EXTERNAL">External</TabsTrigger>
              </TabsList>
              <PortfolioImportStatusComponent
                selectedTab={sourceType as PortfolioSource}
                client={client}
              />
            </div>
          </Tabs>
          <div className="flex flex-col flex-1 gap-6">
            <ConditionalRenderer condition={!hasAum}>
              {sourceType === "INTERNAL" ? (
                <>
                  {isActiveClient(client) && (
                    <OverviewProductBreakdown
                      client={client}
                      breakdown={breakdownBySourceType}
                    />
                  )}
                  <div className="flex-1 flex justify-center">
                    <WhatNext
                      client={client}
                      hasPlacedTransaction={hasPlacedTransaction}
                      isLoadingOrders={isLoadingOrders}
                    />
                  </div>
                </>
              ) : (
                <EmptyExternalPortfolio client={client} />
              )}
            </ConditionalRenderer>

            <ConditionalRenderer condition={hasAum}>
              <div className="flex flex-row bg-muted justify-between rounded-lg px-6 py-4">
                <StatTile
                  label="Total AUM"
                  value={formatAsCrore(currentValue, 2)}
                />
                <StatTile
                  label="Invested"
                  value={formatAsCrore(investedValue, 2)}
                />
                <StatTile
                  label="Returns"
                  value={
                    <div className="flex flex-row gap-1 items-baseline">
                      {formatAsCrore(profitLoss, 1)}
                      <ValueVector className="mt-[0.5]" value={returnPct} />
                    </div>
                  }
                />
                <ConditionalRenderer condition={sourceType !== "EXTERNAL"}>
                  {Number(sip?.monthly_amount) > 0 && (
                    <StatTile
                      label="Monthly SIP"
                      value={formatAsCrore(sip?.monthly_amount ?? 0)}
                    />
                  )}
                  {Number(swp?.monthly_amount) > 0 && (
                    <StatTile
                      label="Monthly SWP"
                      value={formatAsCrore(swp?.monthly_amount ?? 0)}
                    />
                  )}
                  {Number(stp?.monthly_amount) > 0 && (
                    <StatTile
                      label="Monthly STP"
                      value={formatAsCrore(stp?.monthly_amount ?? 0)}
                    />
                  )}
                </ConditionalRenderer>
              </div>
              <AssetAllocation buckets={allocationBuckets} />
              <PortfolioDistribution {...portfolioDistribution} />
              {isActiveClient(client) && (
                <OverviewProductBreakdown
                  client={client}
                  breakdown={breakdownBySourceType}
                />
              )}
            </ConditionalRenderer>
            <ConditionalRenderer condition={hasAum || hasPlacedTransaction}>
              <div className="bg-border w-full h-px" />
              <div className="rounded-lg">
                <OverviewReports client={client} />
              </div>
              <div className="rounded-lg">
                <OverviewRecentActivity clientId={client.id} />
              </div>
            </ConditionalRenderer>
          </div>
        </V2Loader>
      </Page.Body>
    </>
  );
}
